import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'

const scrollFadeOverlayVariants = cva(
  'absolute pointer-events-none transition-opacity duration-200 z-10 to-transparent',
  {
    variants: {
      orientation: {
        horizontal: 'inset-y-0',
        vertical: 'inset-x-0',
      },
      // Fade depth. `size` maps to width (horizontal) or height (vertical) via compoundVariants.
      size: {
        default: '',
        lg: '',
      },
    },
    compoundVariants: [
      { orientation: 'horizontal', size: 'default', class: 'w-4' },
      { orientation: 'horizontal', size: 'lg', class: 'w-8' },
      { orientation: 'vertical', size: 'default', class: 'h-4' },
      { orientation: 'vertical', size: 'lg', class: 'h-8' },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      size: 'default',
    },
  },
)

export interface ScrollFadeOverlayProps {
  canScrollLeft?: boolean
  canScrollRight?: boolean
  canScrollTop?: boolean
  canScrollBottom?: boolean
  gradientFrom?: string
  horizontal?: boolean
  vertical?: boolean
  /** Fade depth: `'default'` = 16px, `'lg'` = 32px (e.g. for large data tables). */
  size?: 'default' | 'lg'
  className?: string
}

export const ScrollFadeOverlay = React.forwardRef<HTMLDivElement, ScrollFadeOverlayProps>(
  (
    {
      canScrollLeft,
      canScrollRight,
      canScrollTop,
      canScrollBottom,
      gradientFrom = 'from-[var(--background)]',
      horizontal = canScrollLeft !== undefined || canScrollRight !== undefined,
      vertical = canScrollTop !== undefined || canScrollBottom !== undefined,
      size = 'default',
      className,
    },
    ref,
  ) => (
    <div ref={ref} className={cn('contents', className)}>
      {horizontal && (
        <>
          <div
            className={cn(
              scrollFadeOverlayVariants({ orientation: 'horizontal', size }),
              'left-0 bg-linear-to-r/srgb',
              gradientFrom,
              canScrollLeft ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div
            className={cn(
              scrollFadeOverlayVariants({ orientation: 'horizontal', size }),
              'right-0 bg-linear-to-l/srgb',
              gradientFrom,
              canScrollRight ? 'opacity-100' : 'opacity-0',
            )}
          />
        </>
      )}
      {vertical && (
        <>
          <div
            className={cn(
              scrollFadeOverlayVariants({ orientation: 'vertical', size }),
              'top-0 bg-linear-to-b/srgb',
              gradientFrom,
              canScrollTop ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div
            className={cn(
              scrollFadeOverlayVariants({ orientation: 'vertical', size }),
              'bottom-0 bg-linear-to-t/srgb',
              gradientFrom,
              canScrollBottom ? 'opacity-100' : 'opacity-0',
            )}
          />
        </>
      )}
    </div>
  ),
)
ScrollFadeOverlay.displayName = 'ScrollFadeOverlay'

export { scrollFadeOverlayVariants }
